import { DeleteOutlined, EditOutlined, FileTextOutlined } from "@ant-design/icons"
import type { TableProps, UploadFile } from "antd"
import { Button, Flex, Popconfirm, Table, Typography } from "antd"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import type { Attachment } from "../../../../../Album/types/attachment.types.ts"
import { getFormatDate } from "../../../../../../utils/date/get-date-safe.ts"
import { showFileAction } from "../../../../../../utils/documents/show-file-action.ts"
import { RenameFileModal } from "../../RenameFileModal.tsx"

const { Text } = Typography

interface FileRow {
  file: UploadFile
  attachment: Attachment | undefined
}

interface ProjectViewAlbumFilesTableProps {
  fileList: UploadFile[]
  currentUserId: string
  deleteAllowed: boolean
  deleteForeignAllowed: boolean
  editAllowed: boolean
  editForeignAllowed: boolean
  deletingIds: Set<string>
  onDelete: (attachmentId: string) => void
  onRename: (attachmentId: string, nextFileName: string) => Promise<void>
  onPreviewFile: (file: UploadFile) => void
}

export function ProjectViewAlbumFilesTable({
  fileList,
  currentUserId,
  deleteAllowed,
  deleteForeignAllowed,
  editAllowed,
  editForeignAllowed,
  deletingIds,
  onDelete,
  onRename,
  onPreviewFile
}: ProjectViewAlbumFilesTableProps) {
  const { t } = useTranslation()
  const [renamingFile, setRenamingFile] = useState<Attachment | null>(null)

  // The album's files arrive as antd upload entries carrying the attachment on
  // `response`; resolve that once rather than in every renderer.
  const rows = useMemo<FileRow[]>(
    () =>
      fileList.map((file) => ({
        file,
        attachment: file.response as Attachment | undefined
      })),
    [fileList]
  )

  const columns = useMemo<TableProps<FileRow>["columns"]>(
    () => [
      {
        title: t("albumView.columns.name"),
        key: "name",
        align: "left",
        render: (_, { file, attachment }) => {
          const fileName = attachment?.original_filename || file.name
          return (
            <Flex align={"center"} gap={"small"}>
              <FileTextOutlined />
              <Button type={"link"} onClick={() => onPreviewFile(file)}>
                {fileName}
              </Button>
            </Flex>
          )
        }
      },
      {
        title: t("albumView.columns.size"),
        key: "size",
        render: (_, { attachment }) =>
          attachment?.formatted_file_size || <Text type={"secondary"}>—</Text>
      },
      {
        title: t("albumView.columns.uploadedBy"),
        key: "uploaded_by",
        render: (_, { attachment }) =>
          attachment?.created_by_name || <Text type={"secondary"}>—</Text>
      },
      {
        title: t("albumView.columns.date"),
        key: "created",
        render: (_, { attachment }) => getFormatDate(attachment?.created) ?? "—"
      },
      {
        title: t("albumView.columns.actions"),
        key: "actions",
        width: 110,
        render: (_, { file, attachment }) => {
          if (!attachment) return null

          // Gated exactly as the card gates them, through the same helper.
          const showRename = showFileAction({
            file: attachment,
            allowed: editAllowed,
            foreignAllowed: editForeignAllowed,
            currentUserId
          })
          const showDelete = showFileAction({
            file: attachment,
            allowed: deleteAllowed,
            foreignAllowed: deleteForeignAllowed,
            currentUserId
          })

          return (
            <Flex align={"center"} justify={"center"}>
              {showRename && (
                <Button
                  type={"text"}
                  icon={<EditOutlined />}
                  onClick={() => setRenamingFile(attachment)}
                  aria-label={t("albumView.rename", {
                    name: attachment.original_filename
                  })}
                />
              )}
              {showDelete && (
                <Popconfirm
                  title={t("albumView.deleteTitle")}
                  okText={t("albumView.deleteConfirm")}
                  cancelText={t("albumView.deleteCancel")}
                  okButtonProps={{ danger: true }}
                  onConfirm={() => onDelete(attachment.id)}
                >
                  <Button
                    type={"text"}
                    danger
                    icon={<DeleteOutlined />}
                    loading={deletingIds.has(file.uid)}
                    aria-label={t("albumView.delete", {
                      name: attachment.original_filename
                    })}
                  />
                </Popconfirm>
              )}
            </Flex>
          )
        }
      }
    ],
    [
      currentUserId,
      deleteAllowed,
      deleteForeignAllowed,
      deletingIds,
      editAllowed,
      editForeignAllowed,
      onDelete,
      t
    ]
  )

  return (
    <>
      {renamingFile && (
        <RenameFileModal
          fileName={renamingFile.original_filename}
          onClose={() => setRenamingFile(null)}
          onSubmit={(next) => onRename(renamingFile.id, next)}
        />
      )}

      <Table<FileRow>
      rowKey={({ file }) => file.uid}
      size={"small"}
      columns={columns}
      dataSource={rows}
      pagination={false}
        scroll={{ x: "max-content" }}
      />
    </>
  )
}
